// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import {
  Users,
  Clock,
  CheckCircle,
  AlertTriangle,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  TrendingUp,
  Calendar,
  History,
  X,
} from "lucide-react";
import { bookingService } from "../../services/bookingService";
import BookingCard from "../../components/admin/BookingCard";
import BookingDetails from "../../components/admin/BookingDetails";
import toast from "react-hot-toast";
import LoadingOverlay from "../../components/LoadingOverlay";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import useTenantBranding from "../../hooks/useTenantBranding";
import useTenants from "../../hooks/useTenants";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import {
  DATE_FILTER_OPTIONS,
  getDefaultDateFilter,
} from "../../utils/dateRanges";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import { getEventTypeOptions } from "../../utils/mappings";

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedBookingUuids, setSelectedBookingUuids] = useState(new Set());
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    problematic: 0,
    problematic_explicit: 0,
    problematic_subtle: 0,
    zur_pruefung: 0,
    zur_pruefung_current_turnus: 0,
    zur_pruefung_last_turnus: 0,
    total_revenue: 0,
    average_booking_value: 0,
    event_types: {},
    packages: {},
    market_types: {},
    event_sizes: {},
  });

  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const { userRoles } = useRoutes();
  const isAdminUser = hasSuperAdminAccess(userRoles || []);

  // Use the tenants hook
  const {
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
    tenantOptions,
    isAllTenantsSelected,
  } = useTenants(getAdminTenantConfig(isAdminUser));

  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNext: false,
    hasPrev: false,
  });

  const [filters, setFilters] = useState({
    status: "active",
    eventType: "",
    search: "",
    llmLabel: "",
    dateFilter: "today",
    tenantId: null,
  });

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const selectedDateFilterOption =
    DATE_FILTER_OPTIONS.find((option) => option.value === filters.dateFilter) ||
    getDefaultDateFilter();

  const { companyName } = useTenantBranding();

  useEffect(() => {
    if (!isAdminUser) {
      if (filters.tenantId !== null) {
        setFilters((prev) => ({ ...prev, tenantId: null }));
      }
      return;
    }

    const isAllSelected =
      typeof isAllTenantsSelected === "function"
        ? isAllTenantsSelected()
        : isAllTenantsSelected;
    const normalizedTenantId =
      !selectedTenant || isAllSelected ? null : selectedTenant;

    if (filters.tenantId === normalizedTenantId) {
      return;
    }

    setFilters((prev) => ({ ...prev, tenantId: normalizedTenantId }));
    setPagination((prev) => ({ ...prev, currentPage: 0 }));
    setSelectedBooking(null);
    setIsDetailsOpen(false);
    setIsMobileFilterOpen(false);
  }, [selectedTenant, isAllTenantsSelected, filters.tenantId, isAdminUser]);

  const toISODate = (dateString) =>
    dateString ? new Date(dateString).toISOString() : undefined;

  const fetchBookings = async () => {
    const isIncompleteCustomRange =
      filters.dateFilter === "custom" && (!customStart || !customEnd);

    if (isIncompleteCustomRange) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters,
      };

      if (!params.status || params.status === "none") {
        params.status = "active";
      }

      if (filters.dateFilter === "custom" && customStart && customEnd) {
        const startDateISO = toISODate(customStart);
        const endDateISO = toISODate(customEnd);
        if (startDateISO && endDateISO) {
          params.startDate = startDateISO;
          params.endDate = endDateISO;
        }
      }

      const response = await bookingService.getBookings(params);

      const bookingsData = response.data || [];
      setBookings(bookingsData);

      setPagination((prev) => ({
        ...prev,
        totalItems: response.total || 0,
        totalPages: Math.ceil((response.total || 0) / pagination.itemsPerPage),
        hasNext: response.has_next || false,
        hasPrev: response.has_prev || false,
      }));
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setError("Failed to load bookings. Please try again.");
      toast.error("Failed to load bookings. Please try again.");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const isIncompleteCustomRange =
      filters.dateFilter === "custom" && (!customStart || !customEnd);

    if (isIncompleteCustomRange) {
      return;
    }

    try {
      const params = {
        status: filters.status || "active",
      };

      if (filters.dateFilter) {
        params.dateFilter = filters.dateFilter;
      }

      if (filters.tenantId) {
        params.tenantId = filters.tenantId;
      }

      if (filters.eventType) {
        params.eventType = filters.eventType;
      }

      if (filters.llmLabel) {
        params.llmLabel = filters.llmLabel;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.problematic) {
        params.problematic = filters.problematic;
      }

      if (filters.zur_pruefung) {
        params.zur_pruefung = filters.zur_pruefung;
      }

      if (filters.dateFilter === "custom" && customStart && customEnd) {
        const startDateISO = toISODate(customStart);
        const endDateISO = toISODate(customEnd);
        if (startDateISO && endDateISO) {
          params.startDate = startDateISO;
          params.endDate = endDateISO;
        }
      }

      const statsData = await bookingService.getBookingStats(params);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load statistics.");
    }
  };

  const handleFilterChange = (newFilters) => {
    const dateFilterChanged = newFilters.dateFilter !== filters.dateFilter;
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 0 }));
    setSelectedBooking(null);
    setIsDetailsOpen(false);
    // Close mobile filter overlay when any filter changes
    setIsMobileFilterOpen(false);
    if (dateFilterChanged && newFilters.dateFilter !== "custom") {
      setCustomStart("");
      setCustomEnd("");
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
    setSelectedBooking(null);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: newLimit,
      currentPage: 0,
    }));
    setSelectedBooking(null);
  };

  const handleBookingSelect = async (booking) => {
    setIsDetailsOpen(true);
    setSelectedBooking(booking);
    setDetailsLoading(true);
    try {
      // Fetch full booking details using the individual booking API
      const fullBookingDetails = await bookingService.getBookingDetails(
        booking.uuid,
      );
      setSelectedBooking(fullBookingDetails);
    } catch (error) {
      console.error("Error fetching booking details:", error);
      // Keep showing the summary data if details fetch fails
      toast.error("Could not load full booking details, showing summary data.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedBooking(null);
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      await bookingService.updateBookingStatus(bookingId, newStatus);
      fetchBookings();
      fetchStats();
      const statusMessages = {
        pending: "Booking status updated to pending!",
        confirmed: "Booking confirmed successfully!",
        completed: "Booking completed successfully!",
        cancelled: "Booking cancelled successfully!",
        rejected: "Booking rejected successfully!",
        expired: "Booking marked as expired!",
      };

      const message =
        statusMessages[newStatus] || "Booking status updated successfully!";
      toast.success(message);
    } catch (error) {
      console.error("Error updating booking status:", error);
      toast.error("Failed to update booking status. Please try again.");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    const bookingToDelete = bookings.find((b) => b.uuid === bookingId);
    const bookingTitle = bookingToDelete?.title || "this booking";

    if (
      window.confirm(
        `Are you sure you want to delete "${bookingTitle}"? This action cannot be undone.`,
      )
    ) {
      try {
        await bookingService.deleteBooking(bookingId);
        fetchBookings();
        fetchStats();
        if (selectedBooking?.uuid === bookingId) {
          setSelectedBooking(null);
          setIsDetailsOpen(false);
        }
        toast.success(`Booking "${bookingTitle}" deleted successfully!`);
      } catch (error) {
        console.error("Error deleting booking:", error);
        toast.error("Failed to delete booking. Please try again.");
      }
    }
  };

  const handleStatCardClick = (status) => {
    let filterStatus = "active";
    let problematic = false;
    let zur_pruefung = false;

    switch (status) {
      case "pending":
        filterStatus = "pending";
        break;
      case "approved":
        filterStatus = "confirmed";
        break;
      case "problematic":
        problematic = true;
        break;
      case "zur_pruefung":
        zur_pruefung = true;
        break;
      case "current_turnus":
        filterStatus = "pending";
        zur_pruefung = true;
        break;
      case "last_turnus":
        filterStatus = "pending";
        zur_pruefung = true;
        break;
      default:
        filterStatus = "active";
    }

    handleFilterChange({
      ...filters,
      status: filterStatus,
      problematic: problematic,
      zur_pruefung: zur_pruefung,
    });
  };

  const handleRefresh = async () => {
    if (filters.dateFilter === "custom" && (!customStart || !customEnd)) {
      toast.error("Bitte wählen Sie einen Start- und Endzeitraum.");
      return;
    }
    toast.loading("Refreshing data...");
    try {
      await Promise.all([fetchBookings(), fetchStats()]);
      toast.success("Data refreshed successfully!");
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data.");
    }
  };

  const clearAllFilters = () => {
    let tenantId = null;

    if (isAdminUser) {
      const isAllSelected =
        typeof isAllTenantsSelected === "function"
          ? isAllTenantsSelected()
          : isAllTenantsSelected;
      tenantId = !selectedTenant || isAllSelected ? null : selectedTenant;
    }

    const clearedFilters = {
      status: "active",
      eventType: "",
      search: "",
      llmLabel: "",
      dateFilter: "today",
      tenantId,
    };
    handleFilterChange(clearedFilters);
    setCustomStart("");
    setCustomEnd("");
    toast.success("All filters cleared!");
  };

  const handleDeleteSelections = () => {
    setIsMobileFilterOpen(false);
    setSelectionMode(true);
    setIsDetailsOpen(false);
    setSelectedBooking(null);
  };

  const handleSelectDisable = () => {
    setSelectionMode(false);
    setSelectedBookingUuids(new Set());
  };

  const handleToggleSelection = (uuid) => {
    setSelectedBookingUuids((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uuid)) {
        newSet.delete(uuid);
      } else {
        newSet.add(uuid);
      }
      return newSet;
    });
  };

  const handleDeleteSelectedBookings = async () => {
    if (selectedBookingUuids.size === 0) {
      toast.error("Bitte wählen Sie mindestens eine Buchung aus.");
      return;
    }

    const count = selectedBookingUuids.size;
    const confirmMessage = `Möchten Sie wirklich ${count} ${
      count === 1 ? "Buchung" : "Buchungen"
    } löschen? Diese Aktion kann nicht rückgängig gemacht werden.`;

    if (window.confirm(confirmMessage)) {
      try {
        const bookingIds = Array.from(selectedBookingUuids);
        await bookingService.bulkDeleteBookings(bookingIds);

        // Clear selections and exit selection mode
        setSelectedBookingUuids(new Set());
        setSelectionMode(false);

        // Refresh data
        fetchBookings();
        fetchStats();

        toast.success(
          `${count} ${count === 1 ? "Buchung wurde" : "Buchungen wurden"} erfolgreich gelöscht!`,
        );
      } catch (error) {
        console.error("Error deleting selected bookings:", error);
        toast.error("Fehler beim Löschen der ausgewählten Buchungen.");
      }
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, [
    filters,
    pagination.currentPage,
    pagination.itemsPerPage,
    customStart,
    customEnd,
  ]);

  const StatCard = ({ title, value, icon: Icon, color, onClick, subtitle }) => (
    <div
      className={`bg-white dark:bg-slate-800 rounded-lg shadow dark:shadow-none dark:border dark:border-slate-700 p-4 cursor-pointer hover:shadow-md dark:hover:border-slate-600 transition-shadow ${onClick ? "hover:bg-gray-50 dark:hover:bg-slate-700/50" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`p-2 rounded-full ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="ml-3">
          <p className="text-xs font-medium text-gray-600 dark:text-slate-400">
            {title}
          </p>
          <p className="text-lg font-semibold text-gray-900 dark:text-slate-100">
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-slate-500">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div
        className={`flex md:gap-0 gap-2 items-center justify-between mt-6 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-lg dark:border dark:border-slate-700 ${isDetailsOpen ? "flex-col gap-2" : "md:flex-row flex-col"}`}
      >
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrev}
            className="px-2 py-2 text-sm font-medium text-gray-500 dark:text-slate-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Vorherige
          </button>
          <span className="text-sm text-gray-700 dark:text-slate-300">
            Seite {pagination.currentPage + 1} von {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNext}
            className="px-2 py-2 text-sm font-medium text-gray-500 dark:text-slate-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            Nächste
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700 dark:text-slate-300">
            Show:
          </span>
          <select
            value={pagination.itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-2 py-1 text-sm"
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} pro Seite
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title={companyName || "Buchungsdashboard"}
        subtitle="Buchungsmanagement"
        tenantOptions={isAdminUser ? tenantOptions : []}
        selectedTenant={isAdminUser ? selectedTenant : null}
        onTenantChange={isAdminUser ? setSelectedTenant : undefined}
        tenantsLoading={isAdminUser ? tenantsLoading : false}
        ranges={DATE_FILTER_OPTIONS.map((r) => r.label)}
        selectedRange={selectedDateFilterOption.label}
        onRangeChange={(value) => {
          const range = DATE_FILTER_OPTIONS.find((r) => r.label === value);
          if (!range) {
            return;
          }
          handleFilterChange({
            ...filters,
            dateFilter: range.value,
          });
          if (range.value !== "custom") {
            setCustomStart("");
            setCustomEnd("");
          }
        }}
        buttons={[
          {
            text: "Aktualisieren",
            icon: (
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
            ),
            onClick: handleRefresh,
            disabled: loading,
            className:
              "bg-primary-color text-white px-3 py-2 rounded-md hover:bg-primary-color-dark flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed",
          },
        ]}
      />

      {/* Custom Date Range Inputs */}
      {filters.dateFilter === "custom" && (
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-end space-x-2">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Custom Date Range:
            </span>
            <input
              type="date"
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span className="text-gray-500 dark:text-slate-500">to</span>
            <input
              type="date"
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="mx-auto px-4 lg:px-4 py-4 text-left">
        {/* Main Stats Row */}
        <div className="grid sm:grid-cols-2 grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <StatCard
            title="Gesamt Buchungen"
            value={stats.total}
            icon={Users}
            color="bg-primary-color"
            onClick={() => handleStatCardClick("all")}
          />
          <StatCard
            title="Zur Prüfung"
            value={stats.zur_pruefung}
            icon={Clock}
            color="bg-primary-color"
            onClick={() => handleStatCardClick("zur_pruefung")}
            subtitle={`${stats.zur_pruefung_current_turnus} aktueller Turnus`}
          />
          <StatCard
            title="Genehmigt"
            value={stats.approved}
            icon={CheckCircle}
            color="bg-primary-color"
            onClick={() => handleStatCardClick("approved")}
          />
          <StatCard
            title="Problematisch"
            value={stats.problematic}
            icon={AlertTriangle}
            color="bg-primary-color"
            onClick={() => handleStatCardClick("problematic")}
            subtitle={`${stats.problematic_explicit} explizit, ${stats.problematic_subtle} subtil`}
          />
        </div>

        {/* Revenue Stats Row */}
        <div className="grid sm:grid-cols-2 grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <StatCard
            title="Gesamt Umsatz"
            value={`€${stats.total_revenue?.toLocaleString() || 0}`}
            icon={DollarSign}
            color="bg-primary-color"
          />
          <StatCard
            title="Durchschnittlicher Buchungswert"
            value={`€${stats.average_booking_value?.toFixed(2) || 0}`}
            icon={TrendingUp}
            color="bg-primary-color"
          />
        </div>

        {/* Detailed Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 hidden">
          <StatCard
            title="Aktueller Turnus"
            value={stats.zur_pruefung_current_turnus}
            icon={Calendar}
            color="bg-indigo-500"
            onClick={() => handleStatCardClick("current_turnus")}
          />
          <StatCard
            title="Letzter Turnus"
            value={stats.zur_pruefung_last_turnus}
            icon={History}
            color="bg-orange-500"
            onClick={() => handleStatCardClick("last_turnus")}
          />
          <StatCard
            title="Explizit Problematisch"
            value={stats.problematic_explicit}
            icon={AlertTriangle}
            color="bg-red-600"
            onClick={() => handleFilterChange({ ...filters, llmLabel: "E" })}
          />
          <StatCard
            title="Subtile Hinweise"
            value={stats.problematic_subtle}
            icon={AlertTriangle}
            color="bg-amber-500"
            onClick={() => handleFilterChange({ ...filters, llmLabel: "V" })}
          />
        </div>

        {/* Desktop Filter Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-4 mb-4 items-center hidden md:block">
          <div className="flex flex-wrap sm:gap-4 gap-2 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400 dark:text-slate-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Filter
              </h2>
            </div>
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Suchen..."
                className="w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-400 rounded-md px-3 py-2"
                value={filters.search}
                onChange={(e) =>
                  handleFilterChange({ ...filters, search: e.target.value })
                }
              />
            </div>
            <select
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm px-3 py-2"
              value={filters.status}
              onChange={(e) =>
                handleFilterChange({ ...filters, status: e.target.value })
              }
            >
              <option value="active">Alle Aktiv</option>
              {/* Values match DB enum booking_status_enum */}
              <option value="pending">Ausstehend</option>
              <option value="confirmed">Bestätigt</option>
              <option value="completed">Abgeschlossen</option>
              <option value="cancelled">Storniert</option>
              <option value="rejected">Abgelehnt</option>
              <option value="expired">Abgelaufen</option>
            </select>

            <select
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm px-3 py-2"
              value={filters.eventType}
              onChange={(e) =>
                handleFilterChange({ ...filters, eventType: e.target.value })
              }
            >
              <option value="">Alle Typen</option>
              {getEventTypeOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm px-3 py-2"
              value={filters.llmLabel}
              onChange={(e) =>
                handleFilterChange({ ...filters, llmLabel: e.target.value })
              }
            >
              <option value="">Alle KI-Bewertungen</option>
              <option value="U">Unbedenklich</option>
              <option value="E">Explizit problematisch</option>
              <option value="V">Subtile/kodierte Hinweise</option>
            </select>
            <select
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md text-sm px-3 py-2"
              value={filters.dateFilter}
              onChange={(e) =>
                handleFilterChange({ ...filters, dateFilter: e.target.value })
              }
            >
              <option value="">Alle Daten</option>
              <option value="this_week">Diese Woche</option>
              <option value="last_week">Letzte Woche</option>
              <option value="next_week">Nächste Woche</option>
              <option value="this_month">Dieser Monat</option>
              <option value="last_month">Letzter Monat</option>
              <option value="next_month">Nächster Monat</option>
              <option value="this_quarter">Dieses Quartal</option>
              <option value="last_quarter">Letztes Quartal</option>
              <option value="next_quarter">Nächstes Quartal</option>
              <option value="this_year">Dieses Jahr</option>
              <option value="this_turnus">Dieser Turnus</option>
              <option value="last_turnus">Letzter Turnus</option>
            </select>

            <button
              onClick={clearAllFilters}
              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-md hover:bg-gray-200 dark:hover:bg-slate-500 transition-colors"
            >
              Filter löschen
            </button>
            <button
              onClick={handleDeleteSelections}
              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-md hover:bg-gray-200 dark:hover:bg-slate-500 transition-colors"
            >
              Auswahlen löschen
            </button>
          </div>
        </div>

        {/* Mobile Filter Button */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setIsMobileFilterOpen(true)}
            className="w-full bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Filter
              </span>
            </div>
            <div className="flex items-center gap-2">
              {(filters.status !== "active" ||
                filters.eventType ||
                filters.search ||
                filters.llmLabel ||
                filters.dateFilter) && (
                <span className="bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  Aktiv
                </span>
              )}
              <ChevronRight className="h-5 w-5 text-gray-400 dark:text-slate-500" />
            </div>
          </button>
        </div>

        {/* Mobile Filter Overlay */}
        {isMobileFilterOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setIsMobileFilterOpen(false)}
            />

            {/* Slide-in Panel */}
            <div className="absolute right-0 top-0 bottom-0 w-full sm:w-96 bg-white dark:bg-slate-800 shadow-xl dark:border-l dark:border-slate-700 overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
                    Filter
                  </h2>
                </div>
                <button
                  onClick={() => setIsMobileFilterOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600 dark:text-slate-400" />
                </button>
              </div>

              {/* Filter Content */}
              <div
                className="p-4 space-y-4"
                style={{ minHeight: "calc(100% - 155px)" }}
              >
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Suchen
                  </label>
                  <input
                    type="text"
                    placeholder="Suchen..."
                    className="w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-400 rounded-md px-3 py-2"
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange({ ...filters, search: e.target.value })
                    }
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Status
                  </label>
                  <select
                    className="w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2"
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange({ ...filters, status: e.target.value })
                    }
                  >
                    <option value="active">Alle Aktiv</option>
                    {/* Values match DB enum booking_status_enum */}
                    <option value="pending">Ausstehend</option>
                    <option value="confirmed">Bestätigt</option>
                    <option value="completed">Abgeschlossen</option>
                    <option value="cancelled">Storniert</option>
                    <option value="rejected">Abgelehnt</option>
                    <option value="expired">Abgelaufen</option>
                  </select>
                </div>

                {/* Event Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Event Typ
                  </label>
                  <select
                    className="w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2"
                    value={filters.eventType}
                    onChange={(e) =>
                      handleFilterChange({
                        ...filters,
                        eventType: e.target.value,
                      })
                    }
                  >
                    <option value="">Alle Typen</option>
                    {getEventTypeOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* LLM Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    KI-Bewertung
                  </label>
                  <select
                    className="w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2"
                    value={filters.llmLabel}
                    onChange={(e) =>
                      handleFilterChange({
                        ...filters,
                        llmLabel: e.target.value,
                      })
                    }
                  >
                    <option value="">Alle KI-Bewertungen</option>
                    <option value="U">Unbedenklich</option>
                    <option value="E">Explizit problematisch</option>
                    <option value="V">Subtile/kodierte Hinweise</option>
                  </select>
                </div>

                {/* Date Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                    Datum
                  </label>
                  <select
                    className="w-full border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2"
                    value={filters.dateFilter}
                    onChange={(e) =>
                      handleFilterChange({
                        ...filters,
                        dateFilter: e.target.value,
                      })
                    }
                  >
                    <option value="">Alle Daten</option>
                    <option value="this_week">Diese Woche</option>
                    <option value="last_week">Letzte Woche</option>
                    <option value="next_week">Nächste Woche</option>
                    <option value="this_month">Dieser Monat</option>
                    <option value="last_month">Letzter Monat</option>
                    <option value="next_month">Nächster Monat</option>
                    <option value="this_quarter">Dieses Quartal</option>
                    <option value="last_quarter">Letztes Quartal</option>
                    <option value="next_quarter">Nächstes Quartal</option>
                    <option value="this_year">Dieses Jahr</option>
                    <option value="this_turnus">Dieser Turnus</option>
                    <option value="last_turnus">Letzter Turnus</option>
                  </select>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4">
                <button
                  onClick={handleDeleteSelections}
                  className="w-full bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-slate-200 py-3 rounded-md hover:bg-gray-200 dark:hover:bg-slate-500 transition-colors font-medium mb-2"
                >
                  Auswahlen löschen
                </button>
                <button
                  onClick={clearAllFilters}
                  className="w-full bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-slate-200 py-3 rounded-md hover:bg-gray-200 dark:hover:bg-slate-500 transition-colors font-medium"
                >
                  Filter löschen
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4 booking-list items-start">
          <div
            className={`transition-all duration-300 ${isDetailsOpen ? "w-1/2" : "w-full"} ${isDetailsOpen && selectedBooking ? "hidden md:block" : ""}`}
          >
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700">
              <div className="p-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex md:flex-row flex-col items-start md:items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                    Buchungen ({pagination.totalItems})
                  </h2>
                  {selectionMode && (
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm text-gray-600 dark:text-slate-400">
                        {selectedBookingUuids.size} ausgewählt
                      </span>
                      {selectedBookingUuids.size > 0 && (
                        <button
                          onClick={handleDeleteSelectedBookings}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 dark:bg-red-500 rounded-md hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                        >
                          Löschen
                        </button>
                      )}
                      <button
                        onClick={handleSelectDisable}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-600 rounded-md hover:bg-gray-200 dark:hover:bg-slate-500 transition-colors"
                      >
                        Abbrechen
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {loading ? (
                <LoadingOverlay loading={loading} />
              ) : error ? (
                <div className="p-6 text-center">
                  <div className="text-red-600 dark:text-red-400 mb-4">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-sm">{error}</p>
                  </div>
                  <button
                    onClick={fetchBookings}
                    className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
                  >
                    Wiederholen
                  </button>
                </div>
              ) : bookings.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-600 dark:text-slate-400">
                    Keine Buchungen gefunden.
                  </p>
                </div>
              ) : (
                <div className="p-4 space-y-3 text-left">
                  {bookings.map((booking) => (
                    <BookingCard
                      key={booking.uuid}
                      selectionMode={selectionMode}
                      booking={booking}
                      openDetails={isDetailsOpen}
                      isSelected={selectedBooking?.uuid === booking.uuid}
                      isCheckboxSelected={selectedBookingUuids.has(
                        booking.uuid,
                      )}
                      onClick={selectionMode ? undefined : handleBookingSelect}
                      onToggleSelection={handleToggleSelection}
                    />
                  ))}
                </div>
              )}
              {renderPagination()}
            </div>
          </div>

          {isDetailsOpen && selectedBooking ? (
            <BookingDetails
              booking={selectedBooking}
              onUpdateStatus={handleUpdateStatus}
              onDelete={handleDeleteBooking}
              onClose={handleCloseDetails}
              detailsLoading={detailsLoading}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Bookings;
